import { Point } from '../types';
import { supabase } from '../lib/supabase';

// Helper to convert Base64 to Blob
function dataURLtoBlob(dataurl: string): Blob {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || '';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
}

export async function uploadToSupabase(base64Image: string, userId: string) {
    const bucketName = 'generated-images';

    try {
        // 1. Upload New Image
        const blob = dataURLtoBlob(base64Image);
        const timestamp = Date.now();
        const filePath = `${userId}/${timestamp}.png`;

        const { error: uploadError } = await supabase.storage
            .from(bucketName)
            .upload(filePath, blob, {
                contentType: 'image/png',
                upsert: false
            });

        if (uploadError) {
            console.error("Supabase Upload Error:", uploadError);
            return;
        }

        // 2. Enforce FIFO Limit via Edge Function
        const { error: pruneError } = await supabase.functions.invoke('manage-storage', {
            method: 'POST',
        });

        if (pruneError) {
            console.warn("Storage Pruning Warning (Edge Function):", pruneError);
        }

    } catch (err) {
        console.error("Unexpected error in uploadToSupabase:", err);
    }
}

export async function addWatermark(imageBase64Src: string): Promise<string> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.error('Could not get canvas context for watermarking.');
                resolve(imageBase64Src);
                return;
            }

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;

            ctx.drawImage(img, 0, 0);

            const fontSize = Math.max(14, Math.min(canvas.width / 70, canvas.height / 50));
            ctx.font = `bold ${fontSize}px sans-serif`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';

            const margin = fontSize;

            ctx.textAlign = 'left';
            const leftText = "https://Dotting.space";
            ctx.fillText(leftText, margin, canvas.height - margin);

            ctx.textAlign = 'right';
            const rightText = "Generated by DOTTING";
            ctx.fillText(rightText, canvas.width - margin, canvas.height - margin);

            const mimeType = imageBase64Src.substring(imageBase64Src.indexOf(':') + 1, imageBase64Src.indexOf(';'));
            resolve(canvas.toDataURL(mimeType));
        };
        img.onerror = () => {
            console.error("Image failed to load for watermarking, returning original image.");
            resolve(imageBase64Src);
        };
        img.src = imageBase64Src;
    });
}

export async function createInpaintedImage(
    originalImageSrc: string,
    points: Point[],
    canvasDimensions: { width: number, height: number }
): Promise<{ maskedImageBase64: string, mimeType: string }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return reject(new Error('Could not get canvas context'));
            }

            ctx.drawImage(img, 0, 0);
            ctx.globalCompositeOperation = 'destination-out';

            const scaleX = img.naturalWidth / canvasDimensions.width;
            const scaleY = img.naturalHeight / canvasDimensions.height;

            ctx.beginPath();
            ctx.moveTo(points[0].x * scaleX, points[0].y * scaleY);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x * scaleX, points[i].y * scaleY);
            }
            ctx.closePath();
            ctx.fillStyle = '#000';
            ctx.fill();

            const mimeType = originalImageSrc.substring(originalImageSrc.indexOf(':') + 1, originalImageSrc.indexOf(';'));
            const maskedImageBase64 = canvas.toDataURL(mimeType).split(',')[1];

            resolve({ maskedImageBase64, mimeType });
        };
        img.onerror = (err) => {
            reject(err);
        };
        img.src = originalImageSrc;
    });
}

export async function compositeInpaintedImage(
    originalImageSrc: string,
    generatedImageSrc: string,
    points: Point[],
    canvasDimensions: { width: number, height: number }
): Promise<string> {
    return new Promise((resolve, reject) => {
        const originalImg = new Image();
        originalImg.crossOrigin = 'anonymous';

        originalImg.onload = () => {
            const generatedImg = new Image();
            generatedImg.crossOrigin = 'anonymous';

            generatedImg.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) return reject(new Error('Could not get canvas context'));

                const w = originalImg.naturalWidth;
                const h = originalImg.naturalHeight;
                canvas.width = w;
                canvas.height = h;

                const maskCanvas = document.createElement('canvas');
                maskCanvas.width = w;
                maskCanvas.height = h;
                const maskCtx = maskCanvas.getContext('2d');
                if (!maskCtx) return reject(new Error('Could not get mask canvas context'));

                const scaleX = w / canvasDimensions.width;
                const scaleY = h / canvasDimensions.height;

                const path = new Path2D();
                path.moveTo(points[0].x * scaleX, points[0].y * scaleY);
                for (let i = 1; i < points.length; i++) {
                    path.lineTo(points[i].x * scaleX, points[i].y * scaleY);
                }
                path.closePath();

                maskCtx.filter = 'none';
                maskCtx.strokeStyle = 'white';
                maskCtx.lineWidth = 1;
                maskCtx.stroke(path);

                maskCtx.fillStyle = 'white';
                maskCtx.fill(path);

                ctx.drawImage(originalImg, 0, 0, w, h);

                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = w;
                tempCanvas.height = h;
                const tempCtx = tempCanvas.getContext('2d');
                if (!tempCtx) return reject(new Error('Could not get temp canvas context'));

                tempCtx.drawImage(generatedImg, 0, 0, w, h);

                tempCtx.globalCompositeOperation = 'destination-in';
                tempCtx.drawImage(maskCanvas, 0, 0, w, h);

                ctx.drawImage(tempCanvas, 0, 0, w, h);

                const mimeType = originalImageSrc.substring(originalImageSrc.indexOf(':') + 1, originalImageSrc.indexOf(';'));
                resolve(canvas.toDataURL(mimeType));
            };

            generatedImg.onerror = (err) => reject(err);
            generatedImg.src = generatedImageSrc;
        };

        originalImg.onerror = (err) => reject(err);
        originalImg.src = originalImageSrc;
    });
}

export async function cropImage(
    originalImageSrc: string,
    points: Point[],
    canvasDimensions: { width: number, height: number }
): Promise<{ croppedImageBase64: string, mimeType: string }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Could not get canvas context'));

            const scaleX = img.naturalWidth / canvasDimensions.width;
            const scaleY = img.naturalHeight / canvasDimensions.height;

            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            points.forEach(p => {
                minX = Math.min(minX, p.x);
                minY = Math.min(minY, p.y);
                maxX = Math.max(maxX, p.x);
                maxY = Math.max(maxY, p.y);
            });

            const sx = minX * scaleX;
            const sy = minY * scaleY;
            const sw = (maxX - minX) * scaleX;
            const sh = (maxY - minY) * scaleY;

            canvas.width = sw;
            canvas.height = sh;

            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

            const mimeType = originalImageSrc.substring(originalImageSrc.indexOf(':') + 1, originalImageSrc.indexOf(';'));
            const croppedImageBase64 = canvas.toDataURL(mimeType).split(',')[1];

            resolve({ croppedImageBase64, mimeType });
        };
        img.onerror = (err) => reject(err);
        img.src = originalImageSrc;
    });
}
